import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { useLayoutEffect, useState } from 'react'

import { SelectInputSM } from '@apps-common/components/elements'
import { FILTER_TAKE_OPTIONS, PAGINATION_PAGE_COUNT } from '@apps-common/config/filter'
import { commonIcons } from '@apps-common/config/icons'

const PaginationSkeleton = () => (
  <div className='mt-2 p-4 flex items-center justify-between w-full animate-pulse'>
    <div className='h-[20px] w-[140px] bg-surface border border-border rounded-lg' />
    <div className='flex items-center justify-center'>
      <div className='mr-[10px] bg-surface border border-border w-[53px] h-[32px] rounded-xl' />
      <div className='h-[32px] w-[32px] bg-surface border border-border rounded-none rounded-l-xl' />
      <div className='h-[32px] w-[32px] bg-surface border-y border-border' />
      <div className='h-[32px] w-[40px] bg-surface border-y border-border' />
      <div className='h-[32px] w-[40px] bg-surface border-y border-border' />
      <div className='h-[32px] w-[40px] bg-surface border-y border-border' />
      <div className='h-[32px] w-[40px] bg-surface border-y border-border' />
      <div className='h-[32px] w-[40px] bg-surface border-y border-border' />
      <div className='h-[32px] w-[40px] bg-surface border-y border-border' />
      <div className='h-[32px] w-[32px] bg-surface border-y border-border' />
      <div className='h-[32px] w-[32px] bg-surface border border-border rounded-none rounded-r-xl' />
    </div>
  </div>
)

interface IPaginationProps {
  loading?: boolean
  data?: number
  pageSkip: number
  pageTake: number
  onTakeChanged: (skip: number) => void
  onSkipChanged: (skip: number) => void
}

export const Pagination: React.FC<IPaginationProps> = ({ loading, data, pageSkip, pageTake, onTakeChanged, onSkipChanged }) => {
  const [pagination, setPagination] = useState<{ page: number; previousPage: number; lastPage: number; pages: number[] }>()

  const getText = () => {
    if (pagination) {
      return (
        <span>
          Displaying page <span className='jost-medium'>{pagination.page}</span> of{' '}
          <span className='jost-medium'>{pagination.lastPage}</span>
        </span>
      )
    }

    return <span />
  }

  const getPageNumbers = (skip: number, take: number, resultCount?: number) => {
    if (!resultCount) return []

    const currentPage = Math.floor(skip / take) + 1
    const totalPages = Math.ceil(resultCount / take)
    const maxPages = PAGINATION_PAGE_COUNT
    const pages: number[] = []

    // If total pages is less than or equal to max pages, show all
    if (totalPages <= maxPages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }

      return pages
    }

    // Calculate start and end pages
    let startPage = Math.max(1, currentPage - Math.floor(maxPages / 2))
    let endPage = startPage + maxPages - 1

    // Adjust if end page exceeds total pages
    if (endPage > totalPages) {
      endPage = totalPages
      startPage = Math.max(1, endPage - maxPages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }

    return pages
  }

  const generatePagination = (skip: number, take: number, resultCount?: number) => {
    const count = resultCount

    if (count) {
      const currentPage = Math.floor(skip / take) + 1
      const lastPage = Math.ceil(count / take)
      const pages = getPageNumbers(skip, take, count)

      return { page: currentPage, previousPage: currentPage - 1, lastPage, pages }
    }

    return { page: 1, previousPage: 1, lastPage: 1, pages: [1] }
  }

  useLayoutEffect(() => {
    const p = generatePagination(pageSkip, pageTake, data)

    setPagination(p)
  }, [pageSkip, pageTake, data])

  return (
    <>
      {loading && <PaginationSkeleton />}
      {!loading && (
        <div className='mt-2 p-4 flex items-center justify-between jost-light text-[0.85em] w-full'>
          <div>{getText()}</div>
          <div className='flex flex-col items-center justify-center'>
            <div className='flex items-center justify-center gap-[1px]'>
              <div>
                <SelectInputSM
                  containerClassName='!m-0 !mr-[10px]'
                  className='!bg-surface !border !border-border !text-text !p-0 !pl-[10px] !pr-[30px] !h-[32px] rounded-xl'
                  iconClassName='!text-text'
                  options={FILTER_TAKE_OPTIONS.map((o) => ({ label: o.toString(), value: o.toString() }))}
                  label='Take'
                  hideError
                  registration={{ value: pageTake.toString(), onChange: (e: any) => onTakeChanged(Number(e.target.value)) }}
                />
              </div>
              <button
                type='button'
                aria-label='First page'
                className='w-[32px] h-[32px] flex items-center justify-center text-text bg-surface hover:bg-surface-2 disabled:opacity-50 rounded-none rounded-l-xl border border-border'
                disabled={!pagination?.page || pagination?.page <= 1}
                onClick={() => onSkipChanged(0)}
              >
                <commonIcons.caretDoubleLeftBold className='text-[1em]' />
              </button>
              <button
                type='button'
                aria-label='Previous page'
                className='w-[32px] h-[32px] flex items-center justify-center text-text bg-surface hover:bg-surface-2 disabled:opacity-50 rounded-none border-y border-r border-border'
                disabled={!pagination?.page || pagination?.page <= 1}
                onClick={() => {
                  onSkipChanged((Math.floor(pageSkip / pageTake) - 1) * pageTake)
                }}
              >
                <commonIcons.caretLeftBold />
              </button>
              <AnimatePresence mode='popLayout'>
                {pagination?.pages?.map((p) => (
                  <motion.button
                    key={`pagination-page-${p}`}
                    type='button'
                    disabled={!p}
                    onClick={() => onSkipChanged((p - 1) * pageTake)}
                    className={classNames(
                      'min-w-[40px] h-[32px] rounded-none disabled:text-text/40 disabled:bg-surface px-[6px] border-y border-r border-border',
                      pagination?.page === p && 'text-on-brand bg-brand pointer-events-none',
                      pagination?.page !== p && 'text-text bg-surface'
                    )}
                  >
                    {p}
                  </motion.button>
                ))}
              </AnimatePresence>
              <button
                type='button'
                aria-label='Next page'
                className='w-[32px] h-[32px] flex items-center justify-center text-text bg-surface hover:bg-surface-2 disabled:opacity-50 rounded-none border-y border-r border-border'
                disabled={!pagination?.page || pagination?.page >= pagination?.lastPage}
                onClick={() => onSkipChanged((pagination?.page || 1 + 1) * pageTake)}
              >
                <commonIcons.caretLeftBold className='rotate-180' />
              </button>
              <button
                type='button'
                aria-label='Last page'
                className='w-[32px] h-[32px] flex items-center justify-center text-text bg-surface hover:bg-surface-2 disabled:opacity-50 rounded-none rounded-r-xl border-y border-r border-border'
                disabled={!pagination?.page || pagination?.page >= pagination?.lastPage}
                onClick={() => {
                  if (pagination?.lastPage) {
                    onSkipChanged((pagination.lastPage - 1) * pageTake)
                  }
                }}
              >
                <commonIcons.caretDoubleLeftBold className='rotate-180' />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
